/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2023, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */
import {
  Component, Inject, OnInit, ViewChild,
} from '@angular/core';
import { SvgIconRegistry } from '@ngneat/svg-icon';
import { fasTrashAltIcon } from 'IconLibrary/fontawesome/solid/trash-alt';
import { fasCheckIcon } from 'IconLibrary/fontawesome/solid/check';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BULB_BUSY_COVER_SERVICE } from 'src/app/angularjs.providers';
import { CropperComponent } from 'angular-cropperjs';
import { ImageType } from 'src/app/image-cropper/image-cropper.component';
import { BulbCKEStateService } from '../../../editor/services/bulb-cke-state.service';

/**
 * Angular component that will house the modal to show orientation options.
 */
@Component({
  selector: 'bulb-orientation-dialog',
  templateUrl: './orientation-dialog.component.html',
  styleUrls: ['./orientation-dialog.component.scss'],
  standalone: false,
})

export class OrientationDialogComponent implements OnInit {

  /**
   * To show orientation modal.
   */
  showOrientationModal: boolean;

  /**
   * Uploaded image url.
   */
  imageUrl : string;

  /**
   * Content block contains data of carousel images.
   */
  contentBlock: any;

  /**
   * Ckeditor instance.
   */
  editorInstance: any;

  /**
   * Contains width of carousel images.
   */
  imageWidth: number;

  /**
   * Contains height of carousel images.
   */
  imageHeight: number;

  /**
   * Contains cropped image data.
   */
  cropData: any;

  /**
   * Contains image element.
   */
  image: any;

  /**
   * Contains orientation of cropper.
   */
  orientation: string;

  /**
   * Contains aspect ratio of orientation.
   */
  aspectRatio: number;

  /**
   * Contains current image index.
   */
  imageIndex: number;

  /**
   * Contains content block id.
   */
  contentBlockId: string;

  /**
   * width of image frame.
   */
  imageFrameWidth: number;

  /**
   * Height of image frame.
   */
  imageFrameHeight: number;

  /**
   * Boolean value which decides to show crop button.
   */
  showCropButton: boolean = false;

  /**
   * Describe the type of image.
   */
  type: ImageType;

  /**
   * The starting index for the cropping process.
   */
  startingIndex: number = 0;

  /**
   * Stores the cropping details of all images.
   */
  croppedImages: any[] = [];

  /**
   * Determines whether to display the image cropper.
   */
  showImageCropper = true;

  /**
   * Indicates whether there are more images left to crop.
   */
  hasMoreImages: boolean = false;

  /**
   * The total number of image assets uploaded together.
   */
  noOfAssetsUploaded: number = 0;

  /**
   * The total number of image assets successfully cropped.
   */
  noOfImagesCropped: number = 0;

  /**
   * Cropper instance.
   */
  @ViewChild('angularCropper') public angularCropper: CropperComponent;

  /**
   * Default Constructor.
   * @param dynamicConfig Dynamic data passed to modal
   * @param dynamicDialogRef Reference to the open dialog.
   * @param svgIconRegistry Icon registry.
   * @param bulbBusyCoverService Busy indicator service.
   */
  constructor(
    private dynamicConfig : DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private svgIconRegistry: SvgIconRegistry,
    private bulbCKEStateService: BulbCKEStateService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
  ) {
    this.svgIconRegistry.register([
      fasTrashAltIcon,
      fasCheckIcon,
    ]);
    this.imageUrl = this.dynamicConfig.data.imageUrl;
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    this.contentBlock = this.dynamicConfig.data.contentBlock;
    this.showOrientationModal = this.dynamicConfig.data.orientationModal;
    this.orientation = this.dynamicConfig.data.orientation;
    this.imageIndex = this.dynamicConfig.data.imageIndex;
    this.startingIndex = this.dynamicConfig.data.imageIndex;
    this.noOfAssetsUploaded = this.dynamicConfig.data.noOfAssets;
    this.contentBlockId = this.dynamicConfig.data.contentBlock?.id;
    if (!this.showOrientationModal) {
      this.updateImageInfo();
      this.imageWidth = this.image?.width;
      this.imageHeight = this.image?.height;
    }

    this.editorInstance = this.dynamicConfig.data.editor;
  }

  /**
   * Updates image information after each image is cropped.
   * Sets the image type based on orientation and updates the current image reference.
   * Also determines if there are more images left to crop.
   * @private
   */
  private updateImageInfo() {
    this.getImageType(this.orientation);
    this.image = this.dynamicConfig.data.contentBlock?.carouselImages[this.imageIndex]?.fullImageAsset;
    this.hasMoreImages = this.noOfImagesCropped < this.noOfAssetsUploaded - 1;
  }

  /**
   * This function dispatch orientation and opens image upload modal .
   */
  uploadImage(orientation) {
    this.orientation = orientation;
    this.bulbCKEStateService.imageCarouselOrientation.next(this.orientation);
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
    const openAssetLibraryEvent = new CustomEvent('openAssetLibraryCarouselType', {
      detail: {
        event,
        eventFiredFromEditor: true,
        orientation,
      },
    });

    window.dispatchEvent(openAssetLibraryEvent);
  }

  /**
   *  Assign aspect ratio according to orientation.
   * @param orientation contains orientation selected by user.
   */
  getImageType(orientation) {
    if (orientation === 'LANDSCAPE') {
      this.type = ImageType.LANDSCAPE;
    } else if (orientation === 'PORTRAIT') {
      this.type = ImageType.PORTRAIT;
    } else if (orientation === 'SQUARE') {
      this.type = ImageType.SQUARE;
    } else {
      this.type = ImageType.FREEFORM;
    }
  }

  /**
   * Delete uncropped image.
   */
  deleteImage() {
    if (this.contentBlock.carouselImages.length <= 1) {
      return window.dispatchEvent(new CustomEvent('blockElementUnload', {
        detail: {
          blockId: this.contentBlockId,
          blockName: 'imageCarousel',
        },
      }));
    }

    const handleCarouselImageDeletionEvent = new CustomEvent('removeImageFromCarousel', {
      detail: {
        eventFiredFromEditor: true,
        blockId: this.contentBlockId,
        imageIndex: this.imageIndex,
      },
    });
    window.dispatchEvent(handleCarouselImageDeletionEvent);
  }

  /**
   * Uploads the cropped image to server.
   */
  cropImage(data: any) {
    this.cropData = data;
    const selectedImageIndex = this.imageIndex;
    const imagePatch = {
      cropRectangle: {
        x: this.cropData.croppedData.x,
        y: this.cropData.croppedData.y,
        width: this.cropData.croppedData.width,
        height: this.cropData.croppedData.height,
      },
      selectedImageIndex,
      rotationDegree: this.cropData.croppedData.rotate ? this.cropData.croppedData.rotate : null,
      unmodifiedRotationDegree: 0,
    };
    this.croppedImages.push(imagePatch);
    const blockId = this.contentBlock.id;
    const editor = this.editorInstance;

    if (this.noOfImagesCropped < this.noOfAssetsUploaded - 1) {
      this.showImageCropper = false;
      setTimeout(() => {
        this.imageIndex++;
        this.noOfImagesCropped++;
        this.updateImageInfo();
        this.showImageCropper = true;
      });
    } else {
      const bulbCropImageEvent = new CustomEvent('bulbCropCarouselImage', {
        detail: {
          croppedImages: this.croppedImages,
          blockId,
          editor,
          selectedImageIndex: this.startingIndex,
          noOfAssetsUploaded: this.noOfAssetsUploaded,
        },
      });
      return window.dispatchEvent(bulbCropImageEvent);
    }
  }

  /**
   * Will close the dialog.
   */
  closeDialog() {
    /*
    Important Note:
    This is needed to remove primeng dynamic dialog from the DOM. For some reason, the component that
    registers the initial opening of the dialog can't seem to close it. This only happens in our AngularJS
    framework.
    */
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
  }

}
